import Button from "@/common/components/Button";
import ModalPassword from "@/common/components/ModalPassword";
import ResumeItem from "@/common/components/ResumeItem";
import { ShowHeader } from "@/common/components/ShowHeader";
import TextArea from "@/common/components/TextArea";
import TextField from "@/common/components/TextField";
import { BillingTransactionIdentifier, BillingTransactionIdentifierErrorResponseData } from "@/common/types/billing/transactionIdentifier.type";
import { formatCurrency, formatDate } from "@/common/utils/formatters";
import { normalizeDate } from "@/common/utils/normalizeDate";
import ModalError from "@/features/refund/components/ModalError";
import ModalSuccess from "@/features/refund/components/ModalSuccess";
import { TransactionInformation } from "@/features/refund/components/transactionInformation";
import { useRefundForm } from "@/features/refund/hooks/useRefundForm";
import { FormProvider } from "react-hook-form";

const RefundCreateMobile = () => {
  const {
    formMethods,
    transactionIdentifiers,
    transactionIdentifiersIsError,
    transactionIdentifiersIsLoading,
    transactionIdentifiersIsPending,
    createRefund,
    createRefundIsPending,
    modalPasswordIsOpen,
    setModalPasswordIsOpen,
    modalSuccessData,
    setModalSuccessData,
    modalErrorData,
    setModalErrorData,
  } = useRefundForm();

  const {
    register,
    getValues,
    reset,
    watch,
    trigger,
    formState: { errors },
  } = formMethods;

  const watchReason = watch('reason');

  const handleOpenPasswordModal = async () => {
    const isValid = await trigger(['transaction_identifier', 'reason']);

    if (isValid) {
      setModalPasswordIsOpen(true);
    }
  }

  return (
    <FormProvider {...formMethods}>
      <div className="flex flex-col items-stretch w-screen h-dvh absolute top-0 left-0">
        <ShowHeader
          backTo="/home"
          title="Estorno"
          className="p-4 mb-0"
          titleClassName="text-base font-normal"
          goBackIconSize={24}
        />

        <div className="h-full overflow-auto py-4 flex flex-col gap-4 p-4">
          <div className="flex flex-col gap-3">
            <p className="text-lg font-bold">Confirmação de segurança</p>

            <TextField
              label="N. da Transação"
              {...register('transaction_identifier')}
              errorMessage={transactionIdentifiersIsError ? 'Transação não encontrada' : ' '}
              isLoading={transactionIdentifiersIsLoading}
              disabled={createRefundIsPending}
              inputMode="numeric"
            />

            {transactionIdentifiers.id && (
              <div className="flex flex-col gap-2">
                <p className="text-base">Dados para estorno</p>

                <TextArea
                  rows={5}
                  label="Motivo"
                  placeholder="Motivo do estorno"
                  {...register('reason')}
                  errorMessage={errors?.reason?.message || ' '}
                  markAsRequired
                />
              </div>
            )}
          </div>

          {transactionIdentifiers.id && (
            <div className="flex flex-col gap-2 h-full">
              <div>
                <p className="text-lg font-bold">Resumo da transação</p>

                {transactionIdentifiers?.eventable?.created_at && (
                  <p className="italic font-light text-sm text-gray-300">Data da transação:
                    {" "} {formatDate(normalizeDate(transactionIdentifiers?.eventable?.created_at))}</p>
                )}
              </div>

              <TransactionInformation
                transactionIdentifiers={transactionIdentifiers}
                transactionIdentifiersIsPending={transactionIdentifiersIsPending}
              />
            </div>
          )}
        </div>

        {transactionIdentifiers.id && (
          <div className="mt-auto bg-white-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <ResumeItem
                  label="Valor total"
                  value={formatCurrency(transactionIdentifiers?.eventable?.total_amount || 0)}
                  valueClassName="-mt-1 font-bold"
                />
              </div>

              <Button
                name="submit"
                type="button"
                variant="error"
                onClick={handleOpenPasswordModal}
                isLoading={createRefundIsPending}
                disabled={!watchReason}
              >
                Estornar transação
              </Button>
            </div>
          </div>
        )}
      </div>

      <ModalPassword
        fieldName="consumption_password"
        isOpen={modalPasswordIsOpen}
        setIsOpen={setModalPasswordIsOpen}
        submit={() => createRefund(getValues())}
      />

      <ModalSuccess
        data={modalSuccessData as BillingTransactionIdentifier}
        setData={setModalSuccessData}
        clearForm={() => reset()}
      />

      <ModalError
        data={modalErrorData as BillingTransactionIdentifierErrorResponseData}
        setData={setModalErrorData}
      />
    </FormProvider>
  );
}

export default RefundCreateMobile;
